"use client";

import type { FormRegularStep } from "./form-types";

import { useCallback } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import {
  getAutoAdvanceRadioInput,
  getVisibleInputs,
  stepInputsRequirePostalLocation,
} from "./form-step-utils";
import { useRegisterKeyboardStepAction } from "./keyboard-step-action-context";
import { useLeadFormScrollTarget } from "./lead-form-scroll-target-context";
import { getPostalLocationValidationErrors } from "./postal-code-config";
import { useTrackedStepAdvance } from "./use-tracked-step-advance";

type UseLeadFormStepKeyboardAdvanceOptions = {
  step: FormRegularStep;
  isActive: boolean;
  isPending: boolean;
  stepAnalyticsName: string;
  stepUserSelection?: Record<string, string | string[]>;
  postalLocationValues: { postalCode: string; location: string };
  onContinue: () => void;
  onValidationError: (errors: Record<string, string[]>) => void;
};

/**
 * Builds the step's Continue handler (postal validation + scrollToTop +
 * onContinue) and registers it as the keyboard-advance action for Enter via
 * KeyboardStepActionContext — but only while the step is active, not
 * pending, and (for auto-advance radio steps) has a selection.
 */
function useLeadFormStepKeyboardAdvance({
  step,
  isActive,
  isPending,
  stepAnalyticsName,
  stepUserSelection,
  postalLocationValues,
  onContinue,
  onValidationError,
}: UseLeadFormStepKeyboardAdvanceOptions) {
  const autoAdvanceInput = getAutoAdvanceRadioInput(step);
  const stepHasAutoAdvance = Boolean(autoAdvanceInput);
  const stepVisibleItems = getVisibleInputs(step.inputs, stepHasAutoAdvance);
  const stepRequiresPostal = stepInputsRequirePostalLocation(stepVisibleItems);
  const { context } = useLocale();
  const { scrollStepRegionToTop } = useLeadFormScrollTarget();

  const { postalCode, location } = postalLocationValues;
  const stepHandleContinue = useCallback(() => {
    if (stepRequiresPostal) {
      const validationErrors = getPostalLocationValidationErrors(
        { location, postalCode },
        context.code,
      );
      if (Object.keys(validationErrors).length > 0) {
        onValidationError(validationErrors);
        return;
      }
    }
    onContinue();
    scrollStepRegionToTop();
  }, [
    stepRequiresPostal,
    postalCode,
    location,
    context.code,
    onContinue,
    onValidationError,
    scrollStepRegionToTop,
  ]);

  // A pointer click on an auto-advance radio option advances immediately, but
  // keyboard selection deliberately does not (see RadioInputOption — arrow-key
  // navigation both moves and selects, so advancing on it would strand later
  // options). Such steps render no Continue button, leaving Enter as the only
  // confirm affordance, so it may advance once a selection actually exists.
  const hasAutoAdvanceSelection = Boolean(
    autoAdvanceInput?.fieldName &&
      stepUserSelection?.[autoAdvanceInput.fieldName],
  );

  const stepAdvance = useTrackedStepAdvance({
    analyticsStepName: stepAnalyticsName,
    analyticsUserSelection: stepUserSelection,
    onContinue: stepHandleContinue,
  });

  const isAwaitingAutoAdvanceSelection =
    stepHasAutoAdvance && !hasAutoAdvanceSelection;
  const canAdvanceOnEnter =
    isActive && !isPending && !isAwaitingAutoAdvanceSelection;

  useRegisterKeyboardStepAction(canAdvanceOnEnter ? stepAdvance : null);

  return {
    autoAdvanceInput,
    stepHandleContinue,
    stepHasAutoAdvance,
    stepVisibleItems,
  };
}

export { useLeadFormStepKeyboardAdvance };
